/* eslint-disable react-refresh/only-export-components */ // FIXME
import { withJsonFormsControlProps } from "@jsonforms/react"
import { ControlElement, JsonFormsRendererRegistryEntry, rankWith, scopeEndsWith, or } from "@jsonforms/core"
import { Form } from "antd"
import { Metric } from "src/api/graphql/graphql.ts"
import { useEffect, useMemo } from "react"
import { startCase } from "lodash-es"
import { JsonFormConfig } from "src/pages/DataAssets/views/Expectations/Expectation/uiForms/ExpectationConfigForm.tsx"
import {
  ColumnType,
  getColumnTypeFromColumnDataType,
} from "src/pages/DataAssets/views/Expectations/Expectation/uiForms/customRenderers/columnType"
import { DataQualityIssue } from "src/global/schemas/expectations/expectation-metadata-data-quality-issues.ts"
import { ColumnNameSelector } from "src/pages/DataAssets/views/Expectations/Expectation/uiForms/customRenderers/ColumnNameSelector.tsx"

export const ColumnNameRegistryEntry: JsonFormsRendererRegistryEntry = {
  tester: rankWith(
    90, //increase rank as needed
    or(
      scopeEndsWith("#/properties/column"),
      scopeEndsWith("#/properties/column_A"),
      scopeEndsWith("#/properties/column_B"),
    ),
  ),
  renderer: withJsonFormsControlProps(ColumnNameControl),
}

type ColumnNameControlProps = {
  handleChange: (path: string, value: unknown) => void
  path: string
  data: Record<string, unknown>
  uischema: ControlElement
  required?: boolean
  config?: JsonFormConfig
}

function ColumnNameControl({ handleChange, path, data, uischema, required, config }: ColumnNameControlProps) {
  const filteredConfig = useMemo(() => {
    if (!config) return undefined
    // remove metrics for columns that do not apply to the selected data quality issue
    const filteredMetrics = config.dataAssetWithLatestMetricRun?.latestMetricRun?.metrics.filter((metric) => {
      const metricColumnType = getColumnTypeFromColumnDataType(metric.columnDataType ?? "")
      return config.dataQualityIssues
        ? getColumnTypesFromDataQualityIssues(config.dataQualityIssues).includes(metricColumnType)
        : true
    })
    return config.dataAssetWithLatestMetricRun?.latestMetricRun
      ? {
          dataAssetWithLatestMetricRun: {
            ...config.dataAssetWithLatestMetricRun,
            latestMetricRun: { ...config.dataAssetWithLatestMetricRun.latestMetricRun, metrics: filteredMetrics ?? [] },
          },
        }
      : { dataAssetWithLatestMetricRun: config.dataAssetWithLatestMetricRun }
  }, [config])

  const options = useMemo(
    () =>
      filteredConfig?.dataAssetWithLatestMetricRun?.latestMetricRun?.metrics.map((metric: Metric) => ({
        value: metric.columnName,
        label: metric.columnName,
        type: metric.columnDataType || undefined,
      })),
    [filteredConfig],
  )

  const form = Form.useFormInstance()

  // using this mostly to set the initial value https://github.com/ant-design/ant-design/issues/22372
  useEffect(() => {
    if (form) {
      form.setFieldsValue({ [path]: data || null })
    }
  }, [data, path, form])

  const label = startCase(path.split(".").pop())
  return (
    <Form.Item
      name={path}
      label={label}
      tooltip={
        (
          uischema as ControlElement & {
            formItemProps: {
              tooltip?: string
            }
          }
        )?.formItemProps?.tooltip
      }
      rules={[
        {
          required: required,
          message: "Column name is required",
        },
      ]}
      validateTrigger="onBlur"
    >
      <ColumnNameSelector options={options} onChange={(value) => handleChange(path, value)} label={label} path={path} />
    </Form.Item>
  )
}

function assertNever(value: never) {
  throw new Error("Unexpected data quality issue: " + value)
}

export function getColumnTypesFromDataQualityIssues(dataQualityIssues: DataQualityIssue[]): ColumnType[] {
  // a type error will be raised here if not all ColumnTypes are handled
  const allColumnTypesObject: { [key in ColumnType]: undefined } = {
    Text: undefined,
    Numeric: undefined,
    Bool: undefined,
    DateTime: undefined,
    Unknown: undefined,
  }
  const allColumnTypes = Object.keys(allColumnTypesObject) as ColumnType[]
  const columnTypes = new Set<ColumnType>()
  for (let i = 0; i < dataQualityIssues.length; i++) {
    let caseColumnTypes: ColumnType[] = []
    const dataQualityIssue = dataQualityIssues[i]
    switch (dataQualityIssue) {
      case "Uniqueness":
        caseColumnTypes = allColumnTypes
        break
      case "Numeric":
        caseColumnTypes = ["Numeric", "DateTime"]
        break
      case "Validity":
        caseColumnTypes = allColumnTypes
        break
      case "Schema":
        caseColumnTypes = allColumnTypes
        break
      case "Completeness":
        caseColumnTypes = allColumnTypes
        break
      case "Volume":
        caseColumnTypes = allColumnTypes
        break
      case "SQL":
        caseColumnTypes = allColumnTypes
        break
      case "Multi-source":
        caseColumnTypes = allColumnTypes
        break
      default:
        // a type error will be raised (before thowing a runtime error)
        // if the data quality issue was not handled
        assertNever(dataQualityIssue)
        break
    }
    caseColumnTypes.forEach((columnType) => columnTypes.add(columnType))
  }
  return Array.from(columnTypes)
}
